import { createTheme, ThemeOptions } from '@mui/material/styles';

const sharedTypography = {
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  h1: { fontSize: '2.25rem', fontWeight: 700, letterSpacing: '-0.02em' },
  h2: { fontSize: '1.75rem', fontWeight: 700, letterSpacing: '-0.01em' },
  h3: { fontSize: '1.5rem', fontWeight: 600 },
  h4: { fontSize: '1.25rem', fontWeight: 600 },
  h5: { fontSize: '1.125rem', fontWeight: 600 },
  h6: { fontSize: '1rem', fontWeight: 600 },
  body1: { fontSize: '1rem', fontWeight: 400, lineHeight: 1.6 },
  body2: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.5 },
  caption: { fontSize: '0.75rem', fontWeight: 400 },
  button: { fontSize: '0.875rem', fontWeight: 600, textTransform: 'none' as const, letterSpacing: '0.02em' },
};

const sharedShape = {
  borderRadius: 12,
};

const lightPalette = {
  mode: 'light' as const,
  primary: { main: '#2563EB', light: '#60A5FA', dark: '#1D4ED8', contrastText: '#FFFFFF' },
  secondary: { main: '#EC4899', light: '#F472B6', dark: '#DB2777' },
  background: { default: '#F8F9FA', paper: '#FFFFFF' },
  text: { primary: '#111827', secondary: '#6B7280' },
  divider: '#E5E7EB',
  success: { main: '#10B981', light: '#D1FAE5', dark: '#059669' },
  error: { main: '#EF4444', light: '#FEE2E2', dark: '#DC2626' },
  warning: { main: '#F59E0B', light: '#FEF3C7', dark: '#D97706' },
  action: { hover: 'rgba(0, 0, 0, 0.04)', selected: 'rgba(37, 99, 235, 0.08)' },
};

const darkPalette = {
  mode: 'dark' as const,
  primary: { main: '#60A5FA', light: '#93C5FD', dark: '#2563EB', contrastText: '#FFFFFF' },
  secondary: { main: '#F472B6', light: '#FBCFE8', dark: '#EC4899' },
  background: { default: '#0F172A', paper: '#1E293B' },
  text: { primary: '#F1F5F9', secondary: '#94A3B8' },
  divider: '#334155',
  success: { main: '#34D399', light: '#064E3B', dark: '#10B981' },
  error: { main: '#F87171', light: '#7F1D1D', dark: '#EF4444' },
  warning: { main: '#FBBF24', light: '#78350F', dark: '#F59E0B' },
  action: { hover: 'rgba(255, 255, 255, 0.06)', selected: 'rgba(96, 165, 250, 0.12)' },
};

const getComponentOverrides = (mode: 'light' | 'dark'): ThemeOptions['components'] => {
  const isLight = mode === 'light';

  return {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          transition: 'background-color 0.2s ease, color 0.2s ease',
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
          border: `1px solid ${isLight ? '#E5E7EB' : '#334155'}`,
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${isLight ? '#E5E7EB' : '#334155'}`,
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: isLight
              ? '0 4px 12px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.06)'
              : '0 4px 12px rgba(0,0,0,0.4), 0 2px 4px rgba(0,0,0,0.3)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 20px',
          fontWeight: 600,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        outlined: {
          borderColor: isLight ? '#D1D5DB' : '#475569',
          '&:hover': {
            borderColor: isLight ? '#9CA3AF' : '#64748B',
            backgroundColor: isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)',
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined' as const },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: isLight ? '#D1D5DB' : '#475569',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: isLight ? '#9CA3AF' : '#64748B',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 6, fontWeight: 500 },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: { borderRadius: 8, overflow: 'auto' },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontWeight: 600,
            color: isLight ? '#6B7280' : '#94A3B8',
            backgroundColor: isLight ? '#F9FAFB' : 'rgba(255,255,255,0.04)',
            borderBottom: `2px solid ${isLight ? '#E5E7EB' : '#334155'}`,
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root': {
            '&:hover': {
              backgroundColor: isLight ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.03)',
            },
          },
          '& .MuiTableCell-root': {
            borderBottom: `1px solid ${isLight ? '#F3F4F6' : '#1E293B'}`,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          border: `1px solid ${isLight ? '#E5E7EB' : '#334155'}`,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { fontSize: '1.25rem', fontWeight: 600 },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: isLight ? '#FFFFFF' : '#1E293B',
          borderBottom: `1px solid ${isLight ? '#E5E7EB' : '#334155'}`,
          color: isLight ? '#111827' : '#F1F5F9',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        outlined: { borderRadius: 8 },
      },
    },
  };
};

export const getTheme = (mode: 'light' | 'dark') =>
  createTheme({
    palette: mode === 'light' ? lightPalette : darkPalette,
    typography: sharedTypography,
    shape: sharedShape,
    components: getComponentOverrides(mode),
  });
